/**
 * MerchantVerification - Merchant verification checklist and Sumsub KYC widget
 */
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FileText, 
  Building2, 
  CreditCard, 
  MapPin, 
  Globe,
  Mail,
  Clock,
  CheckCircle2,
  ArrowLeft,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SumsubVerification from '../components/SumsubVerification';

const MerchantVerification = () => {
  const { user, token } = useAuth();
  const requiredDocuments = [
    {
      icon: FileText,
      title: "Valid Government-Issued ID",
      description: "Passport, national ID, or driver's license",
      color: "text-blue-500"
    },
    {
      icon: Building2,
      title: "Proof of Business Registration",
      description: "Business registration certificate or trade license",
      color: "text-emerald-500"
    },
    {
      icon: CreditCard,
      title: "Bank Account Details",
      description: "Bank statement or voided check for payouts",
      color: "text-purple-500"
    },
    {
      icon: MapPin,
      title: "Proof of Address",
      description: "Utility bill or bank statement (less than 3 months old)",
      color: "text-amber-500"
    },
    {
      icon: Globe,
      title: "Business Information",
      description: "Business description and website URL (if applicable)",
      color: "text-cyan-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">P</span>
              </div>
              <span className="text-xl font-bold">ParachPay</span>
            </Link>
            <Link 
              to="/"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Merchant Verification Checklist
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            To accept payments through ParachPay, all merchants must complete our verification process. 
            Currently, merchant approvals are handled manually by our team.
          </p>
        </div>

        {/* Required Documents Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Required Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {requiredDocuments.map((doc, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-4 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className={`p-2 rounded-lg bg-background ${doc.color}`}>
                    <doc.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{doc.title}</h3>
                    <p className="text-sm text-muted-foreground">{doc.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* How to Submit Section */}
        <Card className="mb-8 border-emerald-200 dark:border-emerald-800">
          <CardHeader className="bg-emerald-50 dark:bg-emerald-900/20 rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              How to Submit
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="text-center p-6 bg-muted/30 rounded-lg mb-4">
              <p className="text-lg mb-2">Please email all verification documents to:</p>
              <a 
                href="mailto:info@parachpay.com"
                className="text-2xl font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                info@parachpay.com
              </a>
            </div>
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Clock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Our team will review your application within <strong className="text-foreground">2-3 business days</strong>. 
                You will receive a confirmation email once your merchant account is approved and activated.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Sumsub KYC/KYB Verification Widget */}
        {user && token ? (
          <SumsubVerification user={user} token={token} />
        ) : (
          <Card className="border-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.1),transparent_50%)]" />
            <CardContent className="py-8 relative">
              <div className="flex items-start gap-5">
                <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/20">
                  <Zap className="w-6 h-6 text-slate-900" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2 text-white">Identity Verification</h3>
                  <p className="text-slate-300 leading-relaxed mb-4">
                    Please log in to access the automated KYC verification powered by Sumsub.
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg font-medium transition-colors"
                  >
                    Log In to Verify
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Help */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Have questions about the verification process?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
            >
              Contact Support
            </Link>
            <Link
              to="/terms"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-input hover:bg-muted rounded-lg font-medium transition-colors"
            >
              View Terms & Conditions
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-emerald-600 rounded flex items-center justify-center">
                <span className="text-white font-bold text-xs">P</span>
              </div>
              <span className="font-semibold">ParachPay</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} ParachPay. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MerchantVerification;
